import {
  Alert,
  Button,
  LinkButton,
  readJson,
  showToast,
  Skeleton,
  useActionKey,
} from "@emdur/ui";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { PermissionPicker } from "../components/PermissionPicker";
import { PermissionList } from "../components/Permissions";
import { useCatalogue } from "../components/useCatalogue";
import { useApi } from "../lib/apiContext";
import {
  type Client,
  appHref,
  clientPath,
  endUserSignupText,
  readClient,
} from "../lib/clients";
import { errorText } from "../lib/errors";
import { APP_KIND } from "./Apps";
import { useRegistry } from "../components/RegistryProvider";
import { RegistrySettings } from "../components/RegistrySettings";
import { TypedConfirmation } from "../components/TypedConfirmation";

export function AppDetail() {
  const { id = "", clientId = "" } = useParams();
  const api = useApi();
  const catalogue = useCatalogue(id);
  const keys = useActionKey();
  const registry = useRegistry();
  const [client, setClient] = useState<Client | null>(null);
  const [etag, setEtag] = useState("");
  const [error, setError] = useState("");
  const [picker, setPicker] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError("");
    try {
      const answer = await readClient(api.fetch, id, clientId);
      setClient(answer.data);
      setEtag(answer.etag);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [api.fetch, id, clientId]);
  useEffect(() => {
    setClient(null);
    void load();
  }, [load]);
  async function revoke() {
    if (!client || keys.sending.current) return;
    keys.sending.current = true;
    setBusy(true);
    const key = keys.keyFor("revoke");
    try {
      const answer = await readJson<Client>(
        api.fetch,
        `${clientPath(id, client.id)}/revoke`,
        { method: "POST", headers: { "Idempotency-Key": key } },
      );
      setClient(answer.data);
      keys.spent("revoke", key);
      setRevoking(false);
      void registry.reload();
      showToast({ title: "App access revoked", tone: "positive" });
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }
  if (!client)
    return error ? (
      <Alert tone="danger">
        {error}
        <Button onPress={() => void load()}>Try again</Button>
      </Alert>
    ) : (
      <Skeleton label="Loading app" />
    );
  return (
    <div className="app-screen flex flex-col gap-5">
      <div>
        <LinkButton variant="quiet" href={appHref(id)}>
          ← Apps
        </LinkButton>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="mb-1 text-sm text-muted">{APP_KIND[client.kind]}</p>
          <h1 className="text-2xl font-strong wrap-anywhere">
            {client.client_name}
          </h1>
        </div>
        {client.state === "active" && (
          <Button
            variant="primary"
            onPress={() => setPicker(true)}
            isDisabled={!catalogue.items}
          >
            Change permissions
          </Button>
        )}
      </div>
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)]">
        <section aria-labelledby="app-permissions" className="min-w-0 py-4">
          <h2 id="app-permissions" className="mb-6 text-xl font-strong">
            What this app can do
          </h2>
          {catalogue.error ? (
            <Alert tone="danger">{catalogue.error}</Alert>
          ) : catalogue.items ? (
            <PermissionList
              permissions={client.capabilities}
              catalogue={catalogue.items}
              reasons={client["x-emdur-reasons"]}
            />
          ) : (
            <Skeleton label="Loading permissions" />
          )}
        </section>
        <RegistrySettings client={client} onError={setError} />
      </div>
      {client.end_user_signup && (
        <section aria-labelledby="app-end-user-signup" className="min-w-0">
          <h2 id="app-end-user-signup" className="mb-2 text-xl font-strong">
            End-user sign-up
          </h2>
          <p className="wrap-anywhere">
            {endUserSignupText(client.end_user_signup)}
          </p>
          <p className="mt-1 text-sm text-muted">
            Only a platform administrator can change this.
          </p>
        </section>
      )}
      {client.state === "active" && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line py-5">
          <div>
            <h2 className="font-strong">End this app's access</h2>
            <p className="mt-1 text-sm text-muted">
              Revoking stops its sessions and API access immediately.
            </p>
          </div>
          <Button
            variant="quiet"
            onPress={() => {
              setError("");
              setRevoking(true);
            }}
          >
            Revoke access
          </Button>
        </div>
      )}
      {error && !revoking && <Alert tone="danger">{error}</Alert>}
      {picker && catalogue.items && (
        <PermissionPicker
          key={etag}
          client={client}
          etag={etag}
          catalogue={catalogue.items}
          isOpen
          onClose={() => setPicker(false)}
          onSaved={(saved, version) => {
            setClient(saved);
            if (version) setEtag(version);
            else void load();
          }}
          onReload={load}
          onPreview={setClient}
        />
      )}
      {revoking && (
        <TypedConfirmation
          title={`Revoke ${client.client_name}?`}
          name={client.client_name}
          action="Revoke access"
          busy={busy}
          error={error}
          onConfirm={() => void revoke()}
          onClose={() => setRevoking(false)}
        >
          <p>
            This app will lose access immediately. Register a new app if you
            need to reconnect it later.
          </p>
        </TypedConfirmation>
      )}
    </div>
  );
}
